
import React, { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import LazyLoadWrapper from "@/components/ui/lazy-load-wrapper";
import { useRoutePreloading } from "@/hooks/useRoutePreloading";
import { useAnalytics } from "@/hooks/useAnalytics";
import { HelmetProvider } from 'react-helmet-async';
import { CookieConsent } from '@/components/CookieConsent';

// Critical pages loaded immediately (always visible)
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ProtectedRoute from '@/components/ProtectedRoute';
import RoleProtectedRoute from '@/components/RoleProtectedRoute';
import AdminProtectedRoute from '@/components/AdminProtectedRoute';

// Lazy-loaded pages (code splitting)
const SaintPiusX = lazy(() => import("./pages/SaintPiusX"));
const SaintOfTheDay = lazy(() => import("./pages/SaintOfTheDay"));
const Catechism = lazy(() => import("./pages/Catechism"));
const QuestMap = lazy(() => import("./pages/QuestMap"));
const QuestDemo = lazy(() => import("./pages/QuestDemo"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Subscription = lazy(() => import("./pages/Subscription"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const Login = lazy(() => import("./pages/Login"));
const SignUp = lazy(() => import("./pages/SignUp"));
const ParentDashboard = lazy(() => import("./pages/ParentDashboard"));
const MyProgress = lazy(() => import("./pages/MyProgress"));
const LessonViewer = lazy(() => import("./pages/LessonViewer"));
const Quiz = lazy(() => import("./pages/Quiz"));
const Lessons = lazy(() => import("./pages/Lessons"));
const LessonMedia = lazy(() => import("./pages/LessonMedia"));
const ImportLessonDemo = lazy(() => import("./pages/ImportLessonDemo"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const AskSaintPiusX = lazy(() => import("./pages/AskSaintPiusX"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const BlogAdmin = lazy(() => import("./pages/BlogAdmin"));
const EmailConfirmation = lazy(() => import("./pages/EmailConfirmation"));
const CheckEmail = lazy(() => import("./pages/CheckEmail"));
const WordSearch = lazy(() => import("./pages/WordSearch"));
const Crossword = lazy(() => import("./pages/Crossword"));
const ColoringPage = lazy(() => import("./pages/ColoringPage"));
const LiturgicalObjects = lazy(() => import("./pages/LiturgicalObjects"));
const DailyTrivia = lazy(() => import("./pages/DailyTrivia"));
const Donate = lazy(() => import("./pages/Donate"));
const DonationSuccess = lazy(() => import("./pages/DonationSuccess"));
const PreGenerateSaints = lazy(() => import("./pages/admin/PreGenerateSaints"));
const PreGenerateTrivia = lazy(() => import("./pages/admin/PreGenerateTrivia"));
const SaintImagesGallery = lazy(() => import("./pages/admin/SaintImagesGallery"));
const SaintInformationGallery = lazy(() => import("./pages/admin/SaintInformationGallery"));
const PreGenerateSaintClues = lazy(() => import("./pages/admin/PreGenerateSaintClues"));
const PreGenerateSaintInfo = lazy(() => import("./pages/admin/PreGenerateSaintInfo"));
const MissingSaintsManager = lazy(() => import("./pages/admin/MissingSaintsManager"));
const GenerateSaintCluesCSV = lazy(() => import("./pages/admin/GenerateSaintCluesCSV"));
const BulkImportSaintClues = lazy(() => import("./pages/admin/BulkImportSaintClues"));
const GenerateSaintInfoCSV = lazy(() => import("./pages/admin/GenerateSaintInfoCSV"));
const BulkImportSaintInfo = lazy(() => import("./pages/admin/BulkImportSaintInfo"));
const RosaryImageMigration = lazy(() => import("./pages/admin/RosaryImageMigration"));
const RosaryArtUpload = lazy(() => import("./pages/admin/RosaryArtUpload"));
const SpotTheHeresy = lazy(() => import("./pages/SpotTheHeresy"));
const GeographyQuest = lazy(() => import("./pages/GeographyQuest"));
const BibleTimeline = lazy(() => import("./pages/BibleTimeline"));
const CatholicDecoder = lazy(() => import("./pages/CatholicDecoder"));
const Admin = lazy(() => import("./pages/Admin"));
const GuessTheSaint = lazy(() => import("./pages/GuessTheSaint"));
const SacramentalsMemory = lazy(() => import("./pages/SacramentalsMemory"));
const SequenceBuilder = lazy(() => import("./pages/SequenceBuilder"));
const EmojiQuiz = lazy(() => import("./pages/EmojiQuiz"));
const VirtueVice = lazy(() => import("./pages/VirtueVice"));
const RosaryArt = lazy(() => import("./pages/RosaryArt"));
const PilgrimsArcade = lazy(() => import("./pages/PilgrimsArcade"));

// Lazy-loaded components
const ProtectedQuiz = lazy(() => import("./components/quiz/ProtectedQuiz"));

import "@/i18n/config";

const queryClient = new QueryClient();

function AppContent() {
  useRoutePreloading();
  useAnalytics();
  
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/saint-pius-x" element={<LazyLoadWrapper><SaintPiusX /></LazyLoadWrapper>} />
      <Route path="/saint-of-the-day" element={<LazyLoadWrapper><SaintOfTheDay /></LazyLoadWrapper>} />
      <Route path="/guess-the-saint" element={<LazyLoadWrapper><GuessTheSaint /></LazyLoadWrapper>} />
      <Route path="/catechism" element={<LazyLoadWrapper><Catechism /></LazyLoadWrapper>} />
      <Route path="/quest-map" element={<LazyLoadWrapper><QuestMap /></LazyLoadWrapper>} />
      <Route path="/demo" element={<LazyLoadWrapper><QuestDemo /></LazyLoadWrapper>} />
      <Route path="/about-us" element={<LazyLoadWrapper><AboutUs /></LazyLoadWrapper>} />
      <Route path="/subscription" element={<LazyLoadWrapper><Subscription /></LazyLoadWrapper>} />
      <Route path="/payment-success" element={<LazyLoadWrapper><PaymentSuccess /></LazyLoadWrapper>} />
      <Route path="/login" element={<LazyLoadWrapper><Login /></LazyLoadWrapper>} />
      <Route path="/signup" element={<LazyLoadWrapper><SignUp /></LazyLoadWrapper>} />
      <Route path="/check-email" element={<LazyLoadWrapper><CheckEmail /></LazyLoadWrapper>} />
      <Route path="/dashboard" element={<LazyLoadWrapper><ParentDashboard /></LazyLoadWrapper>} />
      <Route path="/my-progress" element={<LazyLoadWrapper><MyProgress /></LazyLoadWrapper>} />
      <Route path="/lesson/:moduleId/:lessonId" element={<LazyLoadWrapper><LessonViewer /></LazyLoadWrapper>} />
      <Route path="/quiz/:lessonId" element={<LazyLoadWrapper><ProtectedQuiz /></LazyLoadWrapper>} />
      <Route path="/lessons" element={<LazyLoadWrapper><Lessons /></LazyLoadWrapper>} />
      <Route path="/admin/lesson-media" element={<LazyLoadWrapper><AdminProtectedRoute><LessonMedia /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin" element={<LazyLoadWrapper><AdminProtectedRoute><Admin /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/pre-generate-saints" element={<LazyLoadWrapper><AdminProtectedRoute><PreGenerateSaints /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/pre-generate-trivia" element={<LazyLoadWrapper><AdminProtectedRoute><PreGenerateTrivia /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/saint-images-gallery" element={<LazyLoadWrapper><AdminProtectedRoute><SaintImagesGallery /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/saint-information-gallery" element={<LazyLoadWrapper><AdminProtectedRoute><SaintInformationGallery /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/pre-generate-saint-info" element={<LazyLoadWrapper><AdminProtectedRoute><PreGenerateSaintInfo /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/pre-generate-saint-clues" element={<LazyLoadWrapper><AdminProtectedRoute><PreGenerateSaintClues /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/generate-clues-csv" element={<LazyLoadWrapper><AdminProtectedRoute><GenerateSaintCluesCSV /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/bulk-import-saint-clues" element={<LazyLoadWrapper><AdminProtectedRoute><BulkImportSaintClues /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/generate-saint-info-csv" element={<LazyLoadWrapper><AdminProtectedRoute><GenerateSaintInfoCSV /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/bulk-import-saint-info" element={<LazyLoadWrapper><AdminProtectedRoute><BulkImportSaintInfo /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/missing-saints" element={<LazyLoadWrapper><AdminProtectedRoute><MissingSaintsManager /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/rosary-image-migration" element={<LazyLoadWrapper><AdminProtectedRoute><RosaryImageMigration /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/admin/rosary-art-upload" element={<LazyLoadWrapper><AdminProtectedRoute><RosaryArtUpload /></AdminProtectedRoute></LazyLoadWrapper>} />
      <Route path="/import-demo" element={<LazyLoadWrapper><ImportLessonDemo /></LazyLoadWrapper>} />
      <Route path="/privacy" element={<LazyLoadWrapper><Privacy /></LazyLoadWrapper>} />
      <Route path="/terms" element={<LazyLoadWrapper><Terms /></LazyLoadWrapper>} />
      <Route path="/ask-saint-pius-x" element={<LazyLoadWrapper><AskSaintPiusX /></LazyLoadWrapper>} />
      <Route path="/blog" element={<LazyLoadWrapper><Blog /></LazyLoadWrapper>} />
      <Route path="/blog/:slug" element={<LazyLoadWrapper><BlogPost /></LazyLoadWrapper>} />
      <Route path="/import-post" element={<LazyLoadWrapper><BlogAdmin /></LazyLoadWrapper>} />
          <Route path="/word-search" element={<LazyLoadWrapper><WordSearch /></LazyLoadWrapper>} />
          <Route path="/crossword" element={<LazyLoadWrapper><Crossword /></LazyLoadWrapper>} />
          <Route path="/coloring-page" element={<LazyLoadWrapper><ColoringPage /></LazyLoadWrapper>} />
          <Route path="/liturgical-objects" element={<LazyLoadWrapper><LiturgicalObjects /></LazyLoadWrapper>} />
            <Route path="/sacramentals-memory" element={<LazyLoadWrapper><SacramentalsMemory /></LazyLoadWrapper>} />
            <Route path="/sequence-builder" element={<LazyLoadWrapper><SequenceBuilder /></LazyLoadWrapper>} />
            <Route path="/emoji-quiz" element={<LazyLoadWrapper><EmojiQuiz /></LazyLoadWrapper>} />
            <Route path="/virtue-vice" element={<LazyLoadWrapper><VirtueVice /></LazyLoadWrapper>} />
            <Route path="/rosary-art" element={<LazyLoadWrapper><RosaryArt /></LazyLoadWrapper>} />
            <Route path="/spot-the-heresy" element={<LazyLoadWrapper><SpotTheHeresy /></LazyLoadWrapper>} />
            <Route path="/geography-quest" element={<LazyLoadWrapper><AdminProtectedRoute><GeographyQuest /></AdminProtectedRoute></LazyLoadWrapper>} />
            <Route path="/bible-timeline" element={<LazyLoadWrapper><BibleTimeline /></LazyLoadWrapper>} />
            <Route path="/catholic-decoder" element={<LazyLoadWrapper><CatholicDecoder /></LazyLoadWrapper>} />
            <Route path="/pilgrims-arcade" element={<LazyLoadWrapper><PilgrimsArcade /></LazyLoadWrapper>} />
      <Route path="/daily-trivia" element={<LazyLoadWrapper><DailyTrivia /></LazyLoadWrapper>} />
      <Route path="/donate" element={<LazyLoadWrapper><Donate /></LazyLoadWrapper>} />
      <Route path="/donation-success" element={<LazyLoadWrapper><DonationSuccess /></LazyLoadWrapper>} />
      <Route path="/auth/confirm" element={<LazyLoadWrapper><EmailConfirmation /></LazyLoadWrapper>} />
      <Route path="*" element={<NotFound />} />
      <Route path="/lesson/:lessonId" element={<Navigate to="/quest-map" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider 
          attribute="class" 
          defaultTheme="light" 
          enableSystem={false}
          storageKey="vite-ui-theme"
        >
          <SubscriptionProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <CookieConsent />
                <AppContent />
              </BrowserRouter>
            </TooltipProvider>
          </SubscriptionProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
